import { Component, computed, inject, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { injectQuery } from '@tanstack/angular-query-experimental';

// PrimeNG
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { DropdownModule } from 'primeng/dropdown';
import { MessageModule } from 'primeng/message';
import { MessageService } from 'primeng/api';
import { TreeSelectModule } from 'primeng/treeselect';
import { MultiSelectModule } from 'primeng/multiselect';

// Kanzi
import * as fromCustomer from '@kanzi-apes/kanzi-customer';
import { CustomerService } from '@kanzi-apes/kanzi-customer';
import * as fromAuth from '@kanzi-apes/kanzi-auth';
import { KanziUiModule } from '@kanzi-apes/kanzi-ui';
import { UbicacionesQueriesService } from '../../../queries/ubicaciones-queries.service';
import {
  LocationCreateModel,
  TypeLocationModel,
  CustomerListModel,
  ZoneType,
} from '@kanzi-apes/kanzi-models';
import { Store } from '@ngrx/store';

/**
 * Ubicación Create Component
 *
 * Form to create a new ubicación with parent selection using TreeSelect
 */
@Component({
    selector: 'kanzi-apes-ubicacion-create',
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
        CardModule,
        ButtonModule,
        InputTextModule,
        InputTextareaModule,
        DropdownModule,
        MessageModule,
        TreeSelectModule,
        MultiSelectModule,
        KanziUiModule,
    ],
    providers: [MessageService],
    templateUrl: './ubicacion-create.component.html',
    styleUrls: ['./ubicacion-create.component.scss']
})
export class UbicacionCreateComponent {
  private router = inject(Router);
  private queries = inject(UbicacionesQueriesService);
  private messageService = inject(MessageService);
  private storeData = inject(Store);
  private readonly customerService = inject(CustomerService);

  customerSelected = this.storeData.selectSignal(
    fromCustomer.getCustomerSelected
  );
  userLogged = this.storeData.selectSignal(fromAuth.getAuthUser);

  /**
   * Zone type options for dropdown selection.
   * Maps backend zone type values to user-friendly Spanish labels.
   */
  readonly zoneTypeOptions = [
    { label: 'Recepción', value: 'RECEPTION' },
    { label: 'Recepción de Intercambio', value: 'EXCHANGE_RECEPTION' },
    { label: 'Almacenamiento', value: 'STORAGE' },
    { label: 'Almacenamiento de Buffer', value: 'BUFFER_STORAGE' },
    { label: 'Zona de Tránsito', value: 'TRANSIT' },
    { label: 'Despacho', value: 'DISPATCH' },
    { label: 'Despacho de Intercambio', value: 'EXCHANGE_DISPATCH' },
    { label: 'Control', value: 'CONTROL' },
    { label: 'Control de Pérdidas', value: 'LOSS_CONTROL' },
    { label: 'Destrucción', value: 'DESTRUCTION' },
    { label: 'Donación', value: 'DONATION' },
  ];

  // Determines if customer multi-select should be shown
  // (when logged user's customer has no parent)
  showCustomerSelector = computed(
    () => this.userLogged()?.configs?.customer?.parent == null
  );

  // Query to load available customers (enabled only when multi-select is needed)
  customersQuery = injectQuery<CustomerListModel, Error>(() => ({
    queryKey: ['customers', { page_size: 1000 }],
    queryFn: () =>
      lastValueFrom(this.customerService.listCustomers({ page_size: 1000 })),
    enabled: this.showCustomerSelector(),
    staleTime: 1000 * 60 * 5,
  }));

  // Selected customer IDs for multi-select (plain property for ngModel compatibility)
  selectedCustomerIds: number[] = [];
  // Form model
  ubicacion: LocationCreateModel = {
    name: '',
    display_name: '',
    external_id: '',
    description: '',
    customer_ids: [],
    parent_id: 0,
    type_id: undefined,
    longitude: undefined,
    latitude: undefined,
    zone_type: undefined,
  };

  // Selected parent from tree
  selectedParent = signal<any>(null);

  // Selected type for reactive filtering
  selectedTypeId = signal<number | undefined>(undefined);

  // Queries
  typesQuery = this.queries.useLocationTypes(signal({}));

  // Computed: Get parent type_id from selected type
  parentTypeId = computed(() => {
    const selectedId = this.selectedTypeId();
    if (!selectedId) return undefined;

    const types = this.typesQuery.data()?.results || [];
    const selectedType = types.find((t) => t.id === selectedId);
    return selectedType?.parent?.id;
  });

  // Computed: Filters for parent locations based on selected type
  parentFilters = computed(() => {
    const typeId = this.parentTypeId();
    return typeId ? { type_id: typeId } : null;
  });

  // Query for parent tree (only enabled when parent type is available)
  hasSelectedType = computed(() => {
    const parentId = this.parentTypeId();
    return parentId !== undefined && parentId !== null;
  });
  treeQuery = this.queries.useUbicacionesTree(
    this.parentFilters,
    this.hasSelectedType
  );

  createMutation = this.queries.useCreateUbicacion();

  // Location types for dropdown (exclude WAREHOUSE as it can only be root)
  locationTypes = computed(() => {
    const results = this.typesQuery.data()?.results || [];
    return results
      .filter((t) => t.name !== 'WAREHOUSE')
      .map((t) => ({ label: t.name, value: t.id }));
  });

  // Parent tree for TreeSelect
  parentTree = computed(() => {
    const tree = this.treeQuery.data();
    if (!tree) return [];
    return this.convertToTreeSelectNodes(tree);
  });

  // Loading states
  isTypesLoading = computed(() => this.typesQuery.isPending());
  isTreeLoading = computed(() => this.treeQuery.isPending());
  isCreating = computed(() => this.createMutation.isPending());

  constructor() {
    // Debug effect to track reactive changes
    effect(() => {
      console.log('🔍 Debug Info:', {
        selectedTypeId: this.selectedTypeId(),
        parentTypeId: this.parentTypeId(),
        hasSelectedType: this.hasSelectedType(),
        parentFilters: this.parentFilters(),
        treeQueryEnabled: this.hasSelectedType(),
        treeData: this.treeQuery.data()?.length,
      });
    });
  }

  /**
   * Convert LocationTreeNode[] to TreeSelect nodes
   */
  private convertToTreeSelectNodes(locations: any[]): any[] {
    return locations.map((loc) => this.toTreeSelectNode(loc));
  }

  /**
   * Recursively convert to TreeSelect node format
   */
  private toTreeSelectNode(location: any): any {
    return {
      key: location.id.toString(),
      label: `${location.name} (${
        location.type?.display_name || location.type?.name
      })`,
      data: location.id,
      children:
        location.children?.map((child: any) => this.toTreeSelectNode(child)) ||
        [],
    };
  }

  /**
   * Handle type change to update parent filters
   */
  onTypeChange(typeId: number | undefined): void {
    this.selectedTypeId.set(typeId);
    this.ubicacion.type_id = typeId;
    // Reset selected parent when type changes
    this.selectedParent.set(null);
  }

  /**
   * Validate form
   */
  private isFormValid(): boolean {
    if (!this.ubicacion.name.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'El nombre es requerido',
      });
      return false;
    }

    if (!this.ubicacion.type_id) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'El tipo de ubicación es requerido',
      });
      return false;
    }

    if (!this.selectedParent()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'Debe seleccionar una ubicación padre',
      });
      return false;
    }

    if (this.showCustomerSelector() && this.selectedCustomerIds.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'Debe seleccionar al menos un cliente',
      });
      return false;
    }

    return true;
  }

  /**
   * Save ubicación
   */
  onSave(): void {
    if (!this.isFormValid()) return;

    const dataToSave: LocationCreateModel = {
      ...this.ubicacion,
      display_name: this.ubicacion.display_name || this.ubicacion.name,
      parent_id: parseInt(this.selectedParent()?.key || '0', 10),
      customer_ids: this.showCustomerSelector()
        ? this.selectedCustomerIds
        : [this.customerSelected()?.id || 0],
      zone_type: this.ubicacion.zone_type || null,
    };

    this.createMutation.mutate(dataToSave, {
      onSuccess: (data) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'Ubicación creada correctamente',
        });
        setTimeout(() => {
          this.router.navigate(['/locations/ubicaciones/detail', data.id]);
        }, 1000);
      },
      onError: (error: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.message || 'Error al crear la ubicación',
        });
      },
    });
  }

  /**
   * Cancel and go back
   */
  onCancel(): void {
    this.router.navigate(['/locations/ubicaciones/list']);
  }
}
